import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ReservationFormStates,
  ReservationFormValues,
  ReservationState,
  TimeRangeOption,
} from '../../types/reservation-form';
import { ICalendarEvent } from '../../components/ui/calendar/calendars';
import { EVisitItem } from '../../types/e-visits';
import { getEventPosition } from '../../components/ui/calendar/calendar-week/week-utils';
import {
  IPatientSelectOption,
  ITagSelectOption,
} from '@hilbi/web-common/components/e-visits/select/options/types';

const initialValues: ReservationFormValues = {
  selectedPatient: null,
  selectedTag: null,
  description: '',
  timeRangePickerValue: {
    fromTime: new Date().toISOString(),
    toTime: new Date().toISOString(),
  },
  title: '',
  timeRangeSelectValue: { value: '', label: '', from: '', to: '' },
  dateField: new Date().toISOString(),
  patientDescription: '',
};

const initialState: ReservationState = {
  event: {
    start: new Date().toISOString(),
    end: new Date().toISOString(),
    minTime: '',
    maxTime: '',
    eventData: {} as any,
  },
  visitItem: {
    event: {
      eventId: '',
      from: '',
      to: '',
      description: '',
      expertiseCode: '',
      title: '',
      medicalInstitutionId: '',
    },
    order: {
      id: '',
      patientUserId: '',
      patientDescription: '',
      patientProfile: {
        titleBeforeName: '',
        firstName: '',
        lastName: '',
        titleAfterName: '',
        profileImage: '',
      },
      tag: null,
    },
  },
  patientOptions: [],
  tagOptions: [],
  initialValues,
  submittedValues: null,
  formState: 'initializing',
  errorMessage: '',
  errorMessageTranslated: false,
  description: '',
  title: '',
  selectedTag: null,
  timeRangeOptions: [],
  timeRangeSelectValue: { value: '', label: '', from: '', to: '' },
  timeRangePickerValue: { fromTime: '', toTime: '' },

  displayedDateTime: '',
  displayedDate: '',
  timeRangePickerHidden: true,
  positions: [],
  dayViewScrollTop: 0,
};

const reservationFormSlice = createSlice({
  name: 'reservationForm',
  initialState,
  reducers: {
    setSubmittedValues: (state: ReservationState, action: PayloadAction<ReservationFormValues>) => {
      state.submittedValues = action.payload;
    },
    updateReservationForm: (
      state: ReservationState,
      action: PayloadAction<Partial<ReservationState>>,
    ) => {
      return { ...state, ...action.payload };
    },
    setInitialFormValues: (
      state: ReservationState,
      action: PayloadAction<Partial<ReservationFormValues>>,
    ) => {
      return {
        ...state,
        initialValues: {
          ...state.initialValues,
          ...action.payload,
        },
      };
    },
    setFormState: (state: ReservationState, action: PayloadAction<ReservationFormStates>) => {
      state.formState = action.payload;
    },
    setErrorMessage: (
      state: ReservationState,
      action: PayloadAction<{ message: string; translated?: boolean }>,
    ) => {
      state.errorMessage = action.payload.message;
      state.errorMessageTranslated = !!action.payload.translated;
    },
    setPatientOptions: (state: ReservationState, action: PayloadAction<IPatientSelectOption[]>) => {
      state.patientOptions = action.payload;
    },
    setTagOptions: (state: ReservationState, action: PayloadAction<ITagSelectOption[]>) => {
      state.tagOptions = action.payload;
    },
    setDescription: (state: ReservationState, action: PayloadAction<string>) => {
      state.description = action.payload;
    },
    submitReservationCreateForm: (
      state: ReservationState,
      action: PayloadAction<ReservationFormValues>,
    ) => {
      state.submittedValues = action.payload;
    },
    submitReservationAddForm: (
      state: ReservationState,
      action: PayloadAction<ReservationFormValues>,
    ) => {
      state.submittedValues = action.payload;
    },
    setTitle: (state: ReservationState, action: PayloadAction<string>) => {
      state.title = action.payload;
    },
    setSelectedTag: (state: ReservationState, action: PayloadAction<ITagSelectOption | null>) => {
      state.selectedTag = action.payload;
    },
    setTimeRanges: (state: ReservationState, action: PayloadAction<TimeRangeOption[]>) => {
      state.timeRangeOptions = action.payload;
    },
    setTimeRangePickerHidden: (state: ReservationState, action: PayloadAction<boolean>) => {
      state.timeRangePickerHidden = action.payload;
    },
    setECalendarEvent: (state: ReservationState, action: PayloadAction<ICalendarEvent>) => {
      state.event = action.payload;
    },
    updateCalendarEventTimes: (
      state: ReservationState,
      action: PayloadAction<{ start: string; end: string }>,
    ) => {
      state.event = { ...state.event, ...action.payload };
      state.timeRangePickerValue = {
        fromTime: state.event.start,
        toTime: state.event.end,
      };
    },
    setEVisitItem: (state: ReservationState, action: PayloadAction<EVisitItem>) => {
      state.visitItem = action.payload;
    },
    updateEditedEventPosition: (
      state: ReservationState,
      action: PayloadAction<{ from: string; to: string }>,
    ) => {
      state.positions = [...state.positions].map((positionedEvent) => {
        if (positionedEvent.event.eventData?.event.editing) {
          const { top, height } = getEventPosition({
            from: new Date(action.payload.from),
            to: new Date(action.payload.to),
            minHour: 0,
          });
          positionedEvent.position.top = top + 10; // scrollbar;
          positionedEvent.position.height = height;
          positionedEvent.position.zIndex = 1;
          positionedEvent.event.eventData.event.from = action.payload.from;
          positionedEvent.event.eventData.event.to = action.payload.to;
          state.dayViewScrollTop = top;
          return positionedEvent;
        } else {
          return positionedEvent;
        }
      });
    },
    reset: () => {
      return { ...initialState };
    },
    setupCreateReservationForm: () => {},
    setupReservationEditForm: () => {},
    setupReservationAddForm: () => {},
    submitReservationFormUpdate: (
      state: ReservationState,
      action: PayloadAction<ReservationFormValues>,
    ) => {
      state.submittedValues = action.payload;
    },
    changeReservationFormDate: (state: ReservationState, action: PayloadAction<string>) => {},
    onPatientSearchString: (_: ReservationState, __: PayloadAction<string>) => {},
    clearEvent: (state: ReservationState) => {
      // @ts-ignore
      state.event = undefined;
    },
  },
});

export const { actions, name, reducer } = reservationFormSlice;

export default reducer;
